/**
 * Frosted-glass header on scroll.
 *
 * The site header is solid charcoal at the very top of the page; once the user
 * scrolls past SCROLL_THRESHOLD it gains an `is-scrolled` class that swaps the
 * solid background for a translucent, backdrop-blurred "frosted glass" surface
 * (see the `.site-header.is-scrolled` rule in tailwind.css). White logo + light
 * nav text are kept, so they stay legible against the dark-tinted glass.
 *
 * The class is toggled only when the boolean state actually flips, and the read
 * is rAF-throttled, so the passive scroll listener never forces layout on every
 * scroll event.
 */

const SCROLL_THRESHOLD = 60; // px scrolled before the header switches to glass

export function initHeaderScroll(): void {
  const header = document.querySelector<HTMLElement>('.site-header');
  if (!header) return;

  let scrolled = false;
  let ticking = false;

  const update = (): void => {
    ticking = false;
    const next = window.scrollY > SCROLL_THRESHOLD;
    if (next === scrolled) return;
    scrolled = next;
    header.classList.toggle('is-scrolled', scrolled);
  };

  const onScroll = (): void => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  };

  window.addEventListener('scroll', onScroll, { passive: true });

  // Establish the correct initial state — e.g. when the page is reloaded while
  // already scrolled partway down.
  update();
}
